import { createContext, useContext, type ReactNode } from "react";
import type { SlotContext } from "@b2bportal/core-utilities";
import { FlightFareGridItem } from "@tenant/flights";
import { DeparturePageHeader } from "@/overrides/flights/components/FlightDeparturePage";
import { ReturnPageHeader } from "@/overrides/flights/components/FlightReturnPage";

/**
 * RBC-specific components that can be used across various funnels.
 */
export enum RbcComponent {
  DeparturePageHeader = "DeparturePageHeader",
  ReturnPageHeader = "ReturnPageHeader",
  FlightFareGridItem = "FlightFareGridItem",
}

export const defaultRbcSlots = () => ({
  [RbcComponent.DeparturePageHeader]: DeparturePageHeader,
  [RbcComponent.ReturnPageHeader]: ReturnPageHeader,
  [RbcComponent.FlightFareGridItem]: FlightFareGridItem,
});

export type RbcSlotType = ReturnType<typeof defaultRbcSlots>;

const RbcSlotContext = createContext<SlotContext<RbcSlotType> | undefined>(
  undefined
);

interface RbcSlotProviderProps {
  children: ReactNode;
  context?: SlotContext<RbcSlotType>;
}

/**
 * General RBC Slot Provider that can be used across various funnels.
 *
 * This provider serves as a cross-funnel component system for RBC-specific UI elements
 * that need to be shared across different product flows (packages, flights, hotels, etc.).
 *
 * ## Purpose
 * - Provides RBC-specific components that are reused across multiple funnels
 * - Complements funnel-specific slot providers (e.g., FlightSlotProvider, HotelSlotProvider)
 * - Enables consistent RBC branding and behavior across the entire portal
 *
 * ## Nested Provider Structure
 * RbcSlotProvider is often nested with funnel-specific providers like FlightSlotProvider:
 * ```tsx
 * <RbcSlotProvider>          // Outer: RBC-wide components
 *   <FlightSlotProvider>     // Inner: Flight-specific components
 *     {children}
 *   </FlightSlotProvider>
 * </RbcSlotProvider>
 * ```
 *
 * This nesting is necessary because:
 * 1. **Scope Separation**: RBC components (like DeparturePageHeader) may be used across
 *    multiple funnels, while FlightSlotProvider components are flight-specific
 * 2. **Component Composition**: RBC components can wrap or contain funnel-specific components
 * 3. **Override Hierarchy**: The outer provider establishes RBC-wide defaults that inner
 *    providers can build upon
 *
 * ## Performance Implications
 * - **Minimal overhead**: Each provider adds a React Context, but contexts are lightweight
 * - **Optimized re-renders**: Component maps are typically memoized (useMemo), preventing
 *   unnecessary re-renders unless the component registry actually changes
 * - **Static configuration**: Since slot components rarely change after initial mount,
 *   the nested structure has negligible performance impact
 * - **No prop drilling**: Using context avoids passing components through many layers
 *
 * @example
 * // Used in package flow with nested providers
 * <RbcSlotProvider context={{ components: rbcSlots, providerName: "package-rbc" }}>
 *   <FlightSlotProvider context={{ components: flightSlots, providerName: "package-flights" }}>
 *     <PackageFlightDeparturePage />
 *   </FlightSlotProvider>
 * </RbcSlotProvider>
 */
export const RbcSlotProvider = ({
  context = { components: defaultRbcSlots(), providerName: "rbc" },
  children,
}: RbcSlotProviderProps) => {
  return (
    <RbcSlotContext.Provider value={context}>
      {children}
    </RbcSlotContext.Provider>
  );
};

const useSlotSystem = () => {
  const context = useContext(RbcSlotContext);
  if (context == null) {
    throw new Error("useRbcSlot must be used within a RbcSlotProvider");
  }
  return context;
};

export const useRbcSlot = () => {
  const context = useSlotSystem();
  return context.components;
};
